'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type ModuleKey =
  | 'dashboard' | 'members' | 'attendance' | 'payments' | 'bookings' | 'addons' | 'settings' | 'roles' | 'staff' | 'activity';

export interface ModulePermission {
  module: ModuleKey;
  can_view: boolean;
  can_create: boolean;
  can_edit: boolean;
  can_delete: boolean;
}

export interface StaffProfile {
  id: string;
  arena_id: string;
  full_name: string;
  email: string;
  phone?: string;
  avatar_url?: string;
  is_owner: boolean;
  is_active: boolean;
}

export interface StaffRole {
  id: string;
  arena_id: string;
  name: string;
  description?: string;
  color: string;
  is_system: boolean;
}

interface PermissionsContextValue {
  staffProfile: StaffProfile | null;
  permissions: ModulePermission[];
  assignedRoles: StaffRole[];
  isOwner: boolean;
  isStaff: boolean;
  loading: boolean;
  can: (module: ModuleKey, action: 'view' | 'create' | 'edit' | 'delete') => boolean;
  canView: (module: ModuleKey) => boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextValue>({
  staffProfile: null,
  permissions: [],
  assignedRoles: [],
  isOwner: false,
  isStaff: false,
  loading: false,
  can: () => false,
  canView: () => false,
  refreshPermissions: async () => {},
});

export const usePermissions = () => useContext(PermissionsContext);

const ALL_MODULES: ModuleKey[] = [
  'dashboard', 'members', 'attendance', 'payments',
  'bookings', 'addons', 'settings', 'roles', 'staff', 'activity',
];

const FULL_PERMISSIONS: ModulePermission[] = ALL_MODULES.map((m) => ({
  module: m,
  can_view: true,
  can_create: true,
  can_edit: true,
  can_delete: true,
}));

export const PermissionsProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const [staffProfile, setStaffProfile] = useState<StaffProfile | null>(null);
  const [permissions, setPermissions] = useState<ModulePermission[]>([]);
  const [assignedRoles, setAssignedRoles] = useState<StaffRole[]>([]);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track whether we have already completed at least one successful load.
  // On re-runs (token refresh / USER_UPDATED) we must NOT set loading=true
  // and must NOT clear staffProfile until the new data is ready — that is
  // what caused the transient "Access Restricted" flash.
  const hasLoadedOnceRef = useRef(false);

  // ── FIX: Use user?.id (stable string) as the dep, NOT the full user object.
  // The user object reference changes on every auth state update even when the
  // same user is logged in, which caused loadPermissions to be recreated on
  // every render → useEffect re-fired → setLoading(true) again → infinite loop.
  const userId = user?.id ?? null;

  const loadPermissions = useCallback(async () => {
    // Clear any previous safety timeout
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    // Hard 8-second timeout — loading NEVER hangs forever
    timeoutRef.current = setTimeout(() => {
      setLoading(false);
    }, 8000);

    if (!user) {
      setStaffProfile(null);
      setPermissions([]);
      setAssignedRoles([]);
      setLoading(false);
      hasLoadedOnceRef.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      return;
    }

    const isInitialLoad = !hasLoadedOnceRef.current;
    if (isInitialLoad) {
      setLoading(true);
    }

    try {
      // ── Step 1: Read own staff_profiles row ──────────────────────────────────
      const { data: profile, error: profileError } = await supabase
        .from('staff_profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (profileError) {
        console.error('[PermissionsContext] Error loading staff profile:', profileError.message, profileError.code);

        if (profileError.code === '42P17' || profileError.message?.includes('infinite recursion')) {
          console.warn('[PermissionsContext] Infinite recursion detected in RLS — falling back to metadata mode');
          const meta = user.user_metadata || {};
          const effectiveType = (meta.user_type || meta.role || '').toLowerCase();
          if (effectiveType === 'owner') {
            const syntheticProfile: StaffProfile = {
              id: user.id,
              arena_id: meta.arena_id || '',
              full_name: meta.full_name || meta.ownerName || user.email?.split('@')[0] || 'Owner',
              email: user.email || '',
              is_owner: true,
              is_active: true,
            };
            setStaffProfile(syntheticProfile);
            setPermissions(FULL_PERMISSIONS);
            setAssignedRoles([]);
            hasLoadedOnceRef.current = true;
            setLoading(false);
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            return;
          }
          if (isInitialLoad) {
            setStaffProfile(null);
            setPermissions([]);
            setAssignedRoles([]);
          }
          hasLoadedOnceRef.current = true;
          setLoading(false);
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          return;
        }

        if (!isInitialLoad) {
          setLoading(false);
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          return;
        }
      }

      // ── Step 2: Profile exists ────────────────────────────────────────────────
      if (profile) {
        setStaffProfile(profile);

        if (profile.is_owner) {
          setPermissions(FULL_PERMISSIONS);
          setAssignedRoles([]);
          hasLoadedOnceRef.current = true;
          setLoading(false);
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          return;
        }

        // Non-owner staff: load role-based permissions via RPC
        const { data: perms, error: permsError } = await supabase.rpc('get_staff_permissions', {
          p_user_id: user.id,
        });

        if (permsError) {
          console.error('[PermissionsContext] RPC error:', permsError.message);
        }

        setPermissions((perms as ModulePermission[]) || []);

        const { data: roleAssignments } = await supabase
          .from('staff_role_assignments')
          .select('role_id, staff_roles(id, arena_id, name, description, color, is_system)')
          .eq('staff_id', user.id);

        const roles = roleAssignments
          ?.map((ra: any) => ra.staff_roles)
          .filter(Boolean) as StaffRole[];

        setAssignedRoles(roles || []);
        hasLoadedOnceRef.current = true;
        setLoading(false);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        return;
      }

      // ── Step 3: No profile row — check metadata as fallback ──────────────────
      const meta = user.user_metadata || {};
      const effectiveType = (meta.user_type || meta.role || '').toLowerCase();
      const isOwnerByMeta = effectiveType === 'owner';

      if (isOwnerByMeta) {
        const metaArenaId = meta.arena_id || null;
        let arenaId: string | null = metaArenaId;

        if (!arenaId) {
          const { data: arenaRow } = await supabase
            .from('arenas')
            .select('id')
            .order('created_at', { ascending: true })
            .limit(1)
            .maybeSingle();
          arenaId = arenaRow?.id || null;
        }

        const syntheticProfile: StaffProfile = {
          id: user.id,
          arena_id: arenaId ?? '',
          full_name: meta.full_name || meta.ownerName || user.email?.split('@')[0] || 'Owner',
          email: user.email || '',
          is_owner: true,
          is_active: true,
        };

        if (arenaId) {
          const { error: upsertError } = await supabase.rpc('upsert_owner_staff_profile', {
            p_user_id: user.id,
            p_arena_id: arenaId,
            p_full_name: syntheticProfile.full_name,
            p_email: syntheticProfile.email,
          });

          if (upsertError) {
            const isFkViolation =
              upsertError.code === '23503' || upsertError.message?.includes('foreign key') ||
              upsertError.message?.includes('staff_profiles_id_fkey');

            if (isFkViolation) {
              console.warn(
                '[PermissionsContext] FK violation on upsert — auth user not yet committed. ' +
                'Using synthetic profile for this session. Will retry on next login.'
              );
            } else {
              console.error('[PermissionsContext] Upsert error:', upsertError.message);
            }
          }
        }

        setStaffProfile(syntheticProfile);
        setPermissions(FULL_PERMISSIONS);
        setAssignedRoles([]);
        hasLoadedOnceRef.current = true;
        setLoading(false);
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        return;
      }

      // Not an owner and no profile — not a staff user.
      if (isInitialLoad) {
        setStaffProfile(null);
        setPermissions([]);
        setAssignedRoles([]);
      }
    } catch (err) {
      console.error('[PermissionsContext] Failed to load permissions:', err);
    } finally {
      hasLoadedOnceRef.current = true;
      setLoading(false);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    }
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── FIX: Use a ref so the useEffect below does NOT need loadPermissions in its
  // dep array. Without this, every new loadPermissions reference (from userId change)
  // would cause the effect to re-fire, calling setLoading(true) repeatedly.
  const loadPermissionsRef = useRef(loadPermissions);
  useEffect(() => {
    loadPermissionsRef.current = loadPermissions;
  }, [loadPermissions]);

  useEffect(() => {
    if (!authLoading) {
      loadPermissionsRef.current();
    }
  }, [userId, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const can = useCallback(
    (module: ModuleKey, action: 'view' | 'create' | 'edit' | 'delete'): boolean => {
      if (!staffProfile?.is_active) return false;
      if (staffProfile?.is_owner) return true;
      const perm = permissions.find((p) => p.module === module);
      if (!perm) return false;
      switch (action) {
        case 'view': return perm.can_view;
        case 'create': return perm.can_create;
        case 'edit': return perm.can_edit;
        case 'delete': return perm.can_delete;
        default: return false;
      }
    },
    [staffProfile, permissions]
  );

  const canView = useCallback(
    (module: ModuleKey): boolean => can(module, 'view'),
    [can]
  );

  const value: PermissionsContextValue = {
    staffProfile,
    permissions,
    assignedRoles,
    isOwner: staffProfile?.is_owner ?? false,
    isStaff: !!staffProfile,
    loading,
    can,
    canView,
    refreshPermissions: loadPermissions,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};
